import { HttpClient, HttpContext, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

import { SKIP_ERROR_NOTIFICATION } from '@core/error-handling/services/http-error-notification.interceptor';
import { environment } from '@environments/environment';
import {
  AcompanhamentoApiCampoAdicional,
  AcompanhamentoApiEncerramento,
  AcompanhamentoApiEnturmacao,
  AcompanhamentoApiItemEducacional,
  AcompanhamentoApiItemEducacionalDetalhado,
  AcompanhamentoApiListResponse,
  AcompanhamentoApiPeriodo,
  AcompanhamentoApiRegistro,
  AcompanhamentoApiTurma,
  AcompanhamentoEnturmacoesParams,
  AcompanhamentoListParams
} from '../models/acompanhamento-pedagogico.api.types';

@Injectable({
  providedIn: 'root'
})
export class AcompanhamentoPedagogicoApiService {
  private readonly apiUrl = environment.apiUrl;
  private readonly quietContext = new HttpContext().set(SKIP_ERROR_NOTIFICATION, true);

  constructor(private http: HttpClient) {}

  list(params: AcompanhamentoListParams): Observable<AcompanhamentoApiListResponse<AcompanhamentoApiRegistro>> {
    return this.http.get<AcompanhamentoApiListResponse<AcompanhamentoApiRegistro>>(
      `${this.apiUrl}/commons/acompanhamento-pedagogico`,
      {
        params: this.buildListParams(params),
        context: this.quietContext
      }
    );
  }

  getPeriodos(turmaId: number): Observable<AcompanhamentoApiPeriodo[]> {
    const params = new HttpParams().set('turma', String(turmaId));

    return this.http
      .get<AcompanhamentoApiPeriodo[] | null>(`${this.apiUrl}/commons/periodos-avaliativos`, {
        params,
        context: this.quietContext
      })
      .pipe(map((response) => (Array.isArray(response) ? response : [])));
  }

  getEncerramentos(turmaId: number): Observable<AcompanhamentoApiEncerramento[]> {
    return this.http
      .get<AcompanhamentoApiEncerramento[]>(
        `${this.apiUrl}/commons/encerramento/encerramentos-turma/${turmaId}`,
        { context: this.quietContext }
      )
      .pipe(map((response) => (Array.isArray(response) ? response : [])));
  }

  getTurma(turmaId: number): Observable<AcompanhamentoApiTurma> {
    const params = new HttpParams()
      .set('filter', `id=${turmaId}`)
      .set('offset', '0')
      .set('limit', '1');
    return this.http.get<{ content?: AcompanhamentoApiTurma[] }>(`${this.apiUrl}/context/turma`, {
      params,
      context: this.quietContext
    }).pipe(
      map((response) => response.content?.[0] ?? { id: turmaId })
    );
  }

  getItemEducacionalDetalhado(itemEducacionalId: number): Observable<AcompanhamentoApiItemEducacionalDetalhado> {
    return this.http.get<AcompanhamentoApiItemEducacionalDetalhado>(
      `${this.apiUrl}/professores/item-educacional/${itemEducacionalId}`,
      { context: this.quietContext }
    );
  }

  getItensTurma(turmaId: number): Observable<AcompanhamentoApiItemEducacional[]> {
    return this.http.get<AcompanhamentoApiItemEducacional[]>(`${this.apiUrl}/commons/turma/${turmaId}/filhos`, {
      context: this.quietContext
    });
  }

  getEnturmacoesElegiveis(params: AcompanhamentoEnturmacoesParams): Observable<AcompanhamentoApiEnturmacao[]> {
    return this.http.get<AcompanhamentoApiEnturmacao[]>(
      `${this.apiUrl}/commons/turma/${params.turmaId}/enturmacoes`,
      {
        params: new HttpParams().set('filter', this.buildEnturmacoesFilter(params)),
        context: this.quietContext
      }
    );
  }

  getCamposAdicionais(acompanhamentoId: number): Observable<AcompanhamentoApiCampoAdicional> {
    return this.http.get<AcompanhamentoApiCampoAdicional>(
      `${this.apiUrl}/commons/acompanhamento-pedagogico/campos-adicionais/${acompanhamentoId}`,
      { context: this.quietContext }
    );
  }

  getCamposAdicionaisTemplate(): Observable<AcompanhamentoApiCampoAdicional> {
    return this.http.get<AcompanhamentoApiCampoAdicional>(
      `${this.apiUrl}/commons/acompanhamento-pedagogico/campos-adicionais`,
      { context: this.quietContext }
    );
  }

  update(payload: AcompanhamentoApiRegistro): Observable<AcompanhamentoApiRegistro> {
    return this.http.put<AcompanhamentoApiRegistro>(
      `${this.apiUrl}/commons/acompanhamento-pedagogico/${payload.id}`,
      payload
    );
  }

  createMany(payload: AcompanhamentoApiRegistro): Observable<AcompanhamentoApiRegistro> {
    return this.http.post<AcompanhamentoApiRegistro>(
      `${this.apiUrl}/commons/acompanhamento-pedagogico/varios`,
      payload
    );
  }

  publishToggle(registroId: number): Observable<void> {
    return this.http.put<void>(`${this.apiUrl}/commons/acompanhamento-pedagogico/${registroId}/publicar`, {});
  }

  delete(registroId: number): Observable<void> {
    return this.http.delete<void>(`${this.apiUrl}/commons/acompanhamento-pedagogico/${registroId}`);
  }

  private buildListParams(params: AcompanhamentoListParams): HttpParams {
    let filter = `idTurma = ${params.turmaId}`;

    if (params.data) {
      filter += ` and data = ${params.data}`;
    }

    return new HttpParams()
      .set('filter', filter)
      .set('offset', '0')
      .set('limit', '999');
  }

  private escapeFilterValue(value: string): string {
    return value.replace(/'/g, "''");
  }

  private buildEnturmacoesFilter(params: AcompanhamentoEnturmacoesParams): string {
    let filter =
      `estabelecimento = ${params.estabelecimentoId}` +
      ` and turma = ${params.turmaId}` +
      ` and (dataFim is null or dataFim >= ${params.dataRegistro})` +
      ` and dataInicial <= ${params.dataRegistro}`;

    if (params.etapaMatrizDisciplinaId) {
      filter += ` and etapaMatrizDisciplina = ${params.etapaMatrizDisciplinaId}`;
    }

    if (params.itemEducacionalId) {
      filter += ` and itemEducacionalId = ${params.itemEducacionalId}`;
    }

    if (params.filtrarPublicoAlvo) {
      filter +=
        ' and (possuiDeficiencia=true or possuiTranstornosGlobais=true or possuiAltasHabilidades=true or necessitaAee=true)';
    }

    return filter;
  }
}
